import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useTranslate from '../../hooks/useTranslate';
import Navbar from '../Navbar/MainNavbar/Navbar';
import Footer from '../Footer/Footer';
import resume from '../../constants/resumes.json';
import samira from '../../img/kavak-members/samira.png';
import { resumeLines, datedEntries, projectEntries } from './resumeContent';
import './ResumePage.scss';

const sectionIds = ['experience', 'education', 'recognition', 'projects', 'development'];
export default function ResumePage() {
  const { personSlug } = useParams();
  const { language } = useTranslate();
  const isFa = language.startsWith('fa');
  const person = resume[personSlug]?.[isFa ? 'fa' : 'en'];
  const isSamira = personSlug === 'samira-ramezani';
  const pageRef = useRef(null);
  const [activeSection, setActiveSection] = useState('experience');
  const labels = isFa ? {
    profile: 'تیم کاوک / حوزه درمان', overview: 'معماری درمانی. نگاه یکپارچه.',
    intro: 'معماری فضاهای درمانی، برنامه‌ریزی پزشکی و مدیریت پروژه؛ از طراحی تا اجرا.',
    contact: 'ارتباط مستقیم', explore: 'مرور سوابق', index: 'در این صفحه',
    projects: 'مشارکت در پروژه‌های درمانی', fields: 'حوزه‌های تخصص', work: 'سوابق حرفه‌ای', back: 'بازگشت به خدمات درمانی',
    focus: ['معماری درمانی', 'برنامه‌ریزی پزشکی', 'تجهیزات پزشکی', 'مدیریت پروژه'],
  } : {
    profile: 'KAVAK PEOPLE / HEALTHCARE', overview: 'Healthcare architecture. An integrated perspective.',
    intro: 'Healthcare architecture, medical planning and project management — from design through delivery.',
    contact: 'Get in touch', explore: 'Explore experience', index: 'On this page',
    projects: 'Healthcare project contributions', fields: 'Areas of expertise', work: 'Professional appointments', back: 'Back to Healthcare',
    focus: ['Healthcare architecture', 'Medical planning', 'Medical equipment', 'Project management'],
  };

  useEffect(() => {
    setActiveSection('experience');
    if (!pageRef.current || !('IntersectionObserver' in window)) return;
    const reveals = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          reveals.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });
    pageRef.current.querySelectorAll('[data-resume-reveal]').forEach(el => reveals.observe(el));
    const sections = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveSection(entry.target.dataset.resumeSection);
      });
    }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });
    pageRef.current.querySelectorAll('[data-resume-section]').forEach(el => sections.observe(el));
    return () => { reveals.disconnect(); sections.disconnect(); };
  }, [personSlug, language]);

  const goToSection = (event, id) => {
    const target = pageRef.current?.querySelector(`#resume-${id}`);
    if (!target) return;
    event.preventDefault();
    setActiveSection(id);
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  };

  if (!isSamira || !person) return <><Navbar /><main className="resume-page section__padding">
    {person ? <header className="resume-page__header"><div><h1>{person.name}</h1><p>{person.role}</p></div></header> : <h1>{isFa ? 'رزومه یافت نشد' : 'Resume not found'}</h1>}
    <Link className="custom__button" to="/services/healthcare">{labels.back}</Link>
  </main><Footer /></>;

  const projectCount = projectEntries(person.sections[3].content).length;
  const workCount = datedEntries(person.sections[0].content).length;
  return <><Navbar /><main ref={pageRef} className={`professional-resume ${isFa ? 'rtl-mode' : 'ltr-mode'}`} dir={isFa ? 'rtl' : 'ltr'}>
    <header className="professional-resume__hero">
      <div className="professional-resume__identity">
        <span className="professional-resume__eyebrow"><i aria-hidden="true" />{labels.profile}</span>
        <h1>{person.name}</h1><p className="professional-resume__role">{person.role}</p>
        <div className="professional-resume__rule" aria-hidden="true"><span /></div>
        <h2>{labels.overview}</h2><p className="professional-resume__intro">{labels.intro}</p>
        <div className="professional-resume__actions">
          <a className="professional-resume__primary" href="#resume-experience" onClick={e => goToSection(e, 'experience')}>{labels.explore}<span aria-hidden="true">↓</span></a>
          <a className="professional-resume__text-link" href={`mailto:${person.email}`}>{labels.contact}<span aria-hidden="true">{isFa ? '↖' : '↗'}</span></a>
        </div>
      </div>
      <figure className="professional-resume__portrait">
        <div className="professional-resume__portrait-frame">
          <img src={samira} alt={person.name} loading="eager" />
          <svg className="professional-resume__drawing" viewBox="0 0 400 500" preserveAspectRatio="none" fill="none" aria-hidden="true"><path d="M.5 80V.5H399.5V499.5H.5V80" /><path d="M0 80H16M384 420H400" /></svg>
        </div>
        <figcaption><span>KAVAK</span><span>{isFa ? 'طراحی فضاهای درمانی' : 'Healthcare design'}</span></figcaption>
      </figure>
    </header>
    <div className="professional-resume__highlights" data-resume-reveal>
      <div><strong>{new Intl.NumberFormat(isFa ? 'fa' : 'en').format(projectCount)}</strong><span>{labels.projects}</span></div>
      <div><strong>{new Intl.NumberFormat(isFa ? 'fa' : 'en').format(workCount)}</strong><span>{labels.work}</span></div>
      <div className="professional-resume__focus"><span>{labels.fields}</span><p>{labels.focus.join(isFa ? ' · ' : ' / ')}</p></div>
    </div>
    <div className="professional-resume__body">
      <nav className="professional-resume__index" aria-label={labels.index}>
        <span className="professional-resume__eyebrow">{labels.index}</span>
        {person.sections.map((section, i) => <a key={sectionIds[i]} href={`#resume-${sectionIds[i]}`} onClick={e => goToSection(e, sectionIds[i])} aria-current={activeSection === sectionIds[i] ? 'location' : undefined}><span aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>{section.title}</a>)}
        <a className="professional-resume__email" href={`mailto:${person.email}`} dir="ltr">{person.email}</a>
      </nav>
      <div className="professional-resume__sections">
        {person.sections.map((section, i) => <section key={sectionIds[i]} id={`resume-${sectionIds[i]}`} data-resume-section={sectionIds[i]} data-resume-reveal tabIndex={-1} className="professional-resume__section">
          <header className="professional-resume__section-heading"><span aria-hidden="true">{String(i + 1).padStart(2, '0')}</span><h2>{section.title}</h2><i aria-hidden="true" /></header>
          {i < 2 ? <div className="professional-resume__timeline">{datedEntries(section.content).map((entry, j) => <article key={j} className="professional-resume__entry" data-resume-reveal>
            <span className="professional-resume__timeline-dot" aria-hidden="true" /><h3>{entry.heading}</h3>
            {entry.lines.map((line, k) => <p key={k}>{line}</p>)}
          </article>)}</div> : <ul className={`professional-resume__list ${i === 3 ? 'professional-resume__list--projects' : ''}`}>
            {(i === 3 ? projectEntries(section.content) : i === 2 ? datedEntries(section.content).map(entry => [entry.heading, ...entry.lines].join(' ')) : resumeLines(section.content)).map((line, j) => <li key={j}><span aria-hidden="true">{i === 3 ? String(j + 1).padStart(2, '0') : '—'}</span><p>{line}</p></li>)}
          </ul>}
        </section>)}
        <div className="professional-resume__closing" data-resume-reveal><span className="professional-resume__eyebrow">KAVAK / HEALTHCARE</span><Link className="professional-resume__text-link" to="/services/healthcare">{labels.back}<span aria-hidden="true">{isFa ? '←' : '→'}</span></Link></div>
      </div>
    </div>
  </main><Footer /></>;
}
